import * as ex from 'excalibur';
import { Level } from '../dungeon/Level';
import { TurnManager } from '../core/TurnManager';
import { UIManager } from '../ui/UIManager';
import { GameActor } from '../components/GameActor';
import { Logger } from '../core/Logger';
import { GameEventNames, DieEvent, LevelTransitionEvent, StartTargetingEvent, ItemUseEvent, ItemThrowEvent } from '../core/GameEvents';
import { ItemEntity } from '../factories/ItemFactory';
import { EventBus } from '../core/EventBus';
import { DungeonNavigator } from '../core/DungeonNavigator';
import { LevelManager } from '../core/LevelManager';
import { GameState } from '../core/GameState';
import { DataManager } from '../core/DataManager';
import { InventoryComponent } from '../components/InventoryComponent';
import { ActorDefinition } from '../data/actors';
import { LootSystem } from '../systems/LootSystem';
import { VisibilitySystem } from '../core/Visibility';
import { FogOfWarRenderer } from '../systems/FogOfWarRenderer';
import { VisibilityConfig } from '../config/VisibilityConfig';

export class GameScene extends ex.Scene {
    public level: Level | null = null;
    private hero: GameActor | null = null;
    private fogRenderer: FogOfWarRenderer | null = null;
    private targetingItem: ItemEntity | null = null;
    private reticle: ex.Actor | null = null;

    // Constructor to initialize with a specific level
    constructor(level: Level) {
        super();
        this.level = level;
    }

    public onInitialize(engine: ex.Engine) {
        // Setup level entities once when scene is created
        if (this.level) {
            this.setupLevel(this.level);
        }
    }
    
    public onActivate(context: ex.SceneActivationContext<unknown>): void {
        Logger.info(`[GameScene] Activating scene for level ${this.level?.depth}`);
        
        // 1. Re-attach Hero
        const hero = LevelManager.instance.getPlayer();
        if (hero) {
            this.hero = hero;
            
            // Ensure hero is not in the scene before adding (safety check)
            if (!this.actors.includes(hero)) {
                hero.z = 20; // Ensure hero is above floor items (Z=0) and interactables (Z=5) and mobs (Z=10)
                this.add(hero);
                Logger.info(`[GameScene] Re-attached hero to scene`);
            }
            
            // Update hero position based on entrance/exit
            // If coming from a transition, DungeonNavigator/Level should have set the correct position on the hero
            // or we can set it here if we have context data.
            // For now, assume hero.pos is correct or set by DungeonNavigator before transition.
            
            // Register with managers
            GameState.instance.registerHero(hero);
            
            // Camera
            this.camera.strategy.lockToActor(hero);
            this.camera.zoom = 1.5;
        } else {
            Logger.error('[GameScene] No hero found in LevelManager during activation!');
        }
        
        // 2. Register Level and add Hero to level's actors list
        if (this.level) {
            LevelManager.instance.setCurrentLevel(this.level);
            GameState.instance.registerLevel(this.level);
            DungeonNavigator.instance.discoverFloor(this.level.depth);
            
            // Add hero to level's actors list if not already there
            if (this.hero && !this.level.actors.includes(this.hero)) {
                this.level.actors.push(this.hero);
                Logger.info(`[GameScene] Added hero to level ${this.level.depth} actors list`);
            }
        }
        
        // 3. Register Actors with TurnManager
        // Since onDeactivate clears the TurnManager, we must re-register ALL actors
        // (Hero + Mobs) every time we activate the scene.
        
        // Register Hero
        if (this.hero) {
            TurnManager.instance.registerActor(this.hero);
            Logger.info(`[GameScene] Registered hero with TurnManager`);
        }
        
        // Register Mobs/Actors
        if (this.level) {
            this.level.actors.forEach(actor => {
                if (!actor.isPlayer) {
                    TurnManager.instance.registerActor(actor);
                }
            });
            Logger.info(`[GameScene] Registered ${this.level.actors.length - (this.hero ? 1 : 0)} mobs with TurnManager`);
        }
        
        // 4. Initialize Fog-of-War
        if (this.level && VisibilityConfig.fogOfWarEnabled) {
            // Create fog renderer if not exists
            if (!this.fogRenderer) {
                this.fogRenderer = new FogOfWarRenderer(this.level, this.level.width, this.level.height);
                const fogActors = this.fogRenderer.createFogActors(this);
                fogActors.forEach(actor => this.add(actor));
                Logger.info(`[GameScene] Created fog-of-war renderer`);
            }
            
            // Update visibility from player position
            this.updatePlayerVisibility();
        }
        
        // Update UI (after visibility is calculated so minimap is correct)
        if (this.hero) {
            UIManager.instance.showUI();
            UIManager.instance.update(this.hero);
        }
        
        // 5. Start Turns
        TurnManager.instance.processTurns();
        
        // Listen for death events
        EventBus.instance.on(GameEventNames.Death, this.handleDeath);
        
        // Listen for level transition events
        EventBus.instance.on(GameEventNames.LevelTransition, this.handleLevelTransitionComplete);
        
        // Listen for actor turns to update fog-of-war
        EventBus.instance.on(GameEventNames.ActorTurn, this.handleActorTurn);
        
        // Listen for player movement to update visibility during pathfinding
        EventBus.instance.on(GameEventNames.Movement, this.handlePlayerMovement);

        // Listen for loot generated events
        EventBus.instance.on(GameEventNames.LootGenerated, this.handleLootGenerated);

        // Listen for targeting start
        EventBus.instance.on(GameEventNames.StartTargeting, this.handleStartTargeting);

        // Listen for map reveal
        EventBus.instance.on('level:reveal_all', this.handleRevealAll);
                
        // Input handling for targeting
        context.engine.input.pointers.primary.on('down', this.handlePointerDown);
        
    }

    public onDeactivate(context: ex.SceneActivationContext<unknown>): void {
        Logger.info(`[GameScene] Deactivating scene for level ${this.level?.depth}`);
        
        // 1. Save State - REMOVED
        // Handled by DungeonNavigator.transitionToFloor BEFORE moving the player.
        // Saving here would overwrite the correct state with the new level's player position.
        // DungeonNavigator.instance.saveCurrentLevel();
        
        // 2. Remove Hero from level's actors list
        // This ensures hero is only in one level's actors list at a time
        if (this.hero && this.level) {
            const index = this.level.actors.indexOf(this.hero);
            if (index > -1) {
                this.level.actors.splice(index, 1);
                Logger.info(`[GameScene] Removed hero from level ${this.level.depth} actors list`);
            }
        }
        
        // 3. Stop Turns
        TurnManager.instance.clear(); // This clears the turn queue, but doesn't kill actors
        
        // Cleanup listeners
        EventBus.instance.off(GameEventNames.Death, this.handleDeath);
        EventBus.instance.off(GameEventNames.LevelTransition, this.handleLevelTransitionComplete);
        EventBus.instance.off(GameEventNames.ActorTurn, this.handleActorTurn);
        EventBus.instance.off(GameEventNames.ActorTurn, this.handleActorTurn);
        EventBus.instance.off(GameEventNames.Movement, this.handlePlayerMovement);
        EventBus.instance.off(GameEventNames.Movement, this.handlePlayerMovement);
        EventBus.instance.off(GameEventNames.LootGenerated, this.handleLootGenerated);
        EventBus.instance.off(GameEventNames.StartTargeting, this.handleStartTargeting);
        EventBus.instance.off('level:reveal_all', this.handleRevealAll);
        // We can't easily remove the engine input listener here without reference to the bound function or engine instance cleanly
        // But since onActivate re-adds it, we might duplicate listeners if not careful.
        // Handled by using arrow functions as properties.
        // Wait, engine is passed in onActivate, not available here unless stored.
        // Actually, we should store the handler specifically to remove it.
        // For now, let's just make the handler safe to be called multiple times (it checks targetingItem).
    }

    private handleDeath = (event: DieEvent) => {
         if (this.level) {
             const index = this.level.actors.indexOf(event.actor);
             if (index > -1) {
                 this.level.actors.splice(index, 1);
                 Logger.info(`[GameScene] Removed ${event.actor.name} from level actors`);
             }
             const mobIndex = this.level.mobs.indexOf(event.actor);
             if (mobIndex > -1) {
                 this.level.mobs.splice(mobIndex, 1);
             }
             
             // Generate loot for enemies
             if (!event.actor.isPlayer) {
                 this.generateEnemyLoot(event.actor);
             }
             
             // Check for Game Over
             if (event.actor.isPlayer) {
                 Logger.info('[GameScene] Hero died! Triggering Game Over...');
                 EventBus.instance.emit(GameEventNames.GameOver, {});
             }
         }
    }
    
    private generateEnemyLoot(enemy: GameActor): void {
        // Get actor definition to check for loot table
        const definition = DataManager.instance.query<ActorDefinition>('actor', enemy.name);
        
        if (!definition || !definition.lootTableId) {
            Logger.debug(`[GameScene] No loot table for ${enemy.name}`);
            return;
        }
        
        // Roll for loot drop
        const dropChance = definition.dropChance || 0;
        if (Math.random() > dropChance) {
            Logger.debug(`[GameScene] ${enemy.name} did not drop loot (${dropChance * 100}% chance)`);
            return;
        }
        
        Logger.info(`[GameScene] ${enemy.name} dropped loot!`);
        
        // Generate loot using LootSystem
        const lootItems = LootSystem.instance.generateLootFromEnemy(enemy.name, this.level?.depth || 1);
        
        // Spawn each item at the enemy's death location
        for (const lootItem of lootItems) {
            const gridPos = ex.vec(
                Math.floor(enemy.pos.x / 32),
                Math.floor(enemy.pos.y / 32)
            );
            
            // Spawn via ItemSpawnRequest event
            EventBus.instance.emit(GameEventNames.ItemSpawnRequest, {
                itemId: lootItem.itemId,
                position: gridPos,
                level: this.level,
                count: lootItem.quantity
            });
            
            Logger.info(`[GameScene] Spawned ${lootItem.quantity}x ${lootItem.itemId} at ${gridPos}`);
        }
        
        // Also drop any items in inventory (e.g. keys held by bosses)
        const inventory = enemy.getGameComponent<InventoryComponent>('inventory');
        if (inventory) {
            inventory.items.forEach(item => {
                 const gridPos = ex.vec(
                    Math.floor(enemy.pos.x / 32),
                    Math.floor(enemy.pos.y / 32)
                );
                
                EventBus.instance.emit(GameEventNames.ItemSpawnRequest, {
                    itemId: item.id,
                    position: gridPos,
                    level: this.level,
                    count: 1 // Items are instances, count 1
                });
                Logger.info(`[GameScene] Dropped inventory item ${item.id} from ${enemy.name}`);
            });
        }
    }
    
    /**
     * Update player visibility and fog-of-war
     */
    private updatePlayerVisibility(): void {
        if (!this.hero || !this.level || !VisibilityConfig.enabled) return;
        
        // Get visible tiles from player position
        const visibleTiles = VisibilitySystem.instance.getVisibleTiles(this.hero, this, VisibilityConfig.playerViewRadius);
        
        // Update level's visible and discovered tiles
        this.level.visibleTiles = visibleTiles;
        visibleTiles.forEach(tile => this.level!.discoveredTiles.add(tile));
        
        // Update fog renderer
        if (this.fogRenderer) {
            this.fogRenderer.update();
        }
        
        // Hide/show enemies based on visibility
        if (VisibilityConfig.fogOfWarEnabled) {
            this.level.actors.forEach(actor => {
                if (!actor.isPlayer && !actor.isDead) {
                    const key = `${actor.gridPos.x},${actor.gridPos.y}`;
                    actor.graphics.visible = this.level!.visibleTiles.has(key);
                }
            });
        }
        
        Logger.debug(`[GameScene] Updated visibility: ${visibleTiles.size} tiles visible, ${this.level.discoveredTiles.size} discovered`);
        
        // Update Minimap/UI
        if (this.hero) {
            UIManager.instance.update(this.hero);
        }
    }

    private handleLevelTransitionComplete = (event: LevelTransitionEvent) => {
        Logger.info(`[GameScene] Level transition requested: ${event.direction} to ${event.toLevel}`);
        // Transition is handled by DungeonNavigator switching scenes
        // We just need to ensure we don't block it.
    }

    private handleActorTurn = (event: any) => {
        if (event.actor?.isPlayer) {
            this.updatePlayerVisibility();
        }
    }
    
    private handlePlayerMovement = (event: any) => {
        // Update visibility after each player movement (including pathfinding steps)
        if (event.actor?.isPlayer) {
            this.updatePlayerVisibility();
        }
    }

    private handleLootGenerated = (event: any) => {
        Logger.info(`[GameScene] Handling LootGenerated event with ${event.items.length} items`);
        
        if (!this.level) return;

        event.items.forEach((item: any) => {
            EventBus.instance.emit(GameEventNames.ItemSpawnRequest, {
                itemId: item.itemId,
                position: event.position, // Use event position
                level: this.level,
                count: item.quantity
            });
        });

    }

    private handleStartTargeting = (event: StartTargetingEvent) => {
        Logger.info(`[GameScene] Targeting mode enabled for ${event.item.getDisplayName()}`);
        this.targetingItem = event.item;
        
        // Show reticle or change cursor (TODO: implementing a visual reticle would be nice)
        // For now, we rely on the click handler.
        if (typeof document !== 'undefined') {
            document.body.style.cursor = 'crosshair';
        }
        
        EventBus.instance.emit(GameEventNames.Log, {
            message: 'Select a target...',
            category: 'Combat',
            color: '#ffff00'
        } as any);
    }
    
    private handlePointerDown = (event: ex.PointerEvent) => {
        if (!this.targetingItem || !this.hero) return;
        
        // Convert screen to world
        const worldPos = this.engine.screen.screenToWorldCoordinates(event.screenPos);
        
        Logger.info(`[GameScene] Targeting click at ${worldPos}`);
        
        // Emit throw event with target
        EventBus.instance.emit(GameEventNames.ItemThrow, new ItemThrowEvent(
            this.hero,
            this.targetingItem,
            worldPos
        ));
        
        // Reset state
        this.targetingItem = null;
        if (typeof document !== 'undefined') {
            document.body.style.cursor = 'default';
        }
    }


    private handleRevealAll = () => {
        if (!this.level) return;
        
        Logger.info('[GameScene] Revealing entire map!');
        
        // Mark all tiles as discovered
        for (let x = 0; x < this.level.width; x++) {
            for (let y = 0; y < this.level.height; y++) {
                this.level.discoveredTiles.add(`${x},${y}`);
            }
        }
        
        // Update fog if active
        if (this.fogRenderer) {
            this.fogRenderer.update();
        }
        
        // Force UI update (minimap)
        if (this.hero) {
            UIManager.instance.update(this.hero);
        }
    }

    private async setupLevel(level: Level): Promise<void> {
        Logger.info(`[GameScene] Setting up level ${level.depth} with ${level.actors.length} actors`);
        
        // Add tilemaps
        if (!this.tileMaps.includes(level.floorMap)) this.add(level.floorMap);
        if (!this.tileMaps.includes(level.objectMap)) this.add(level.objectMap);
        
        // Add actors (EXCEPT HERO - Hero is added in onActivate)
        // Add actors (EXCEPT HERO - Hero is added in onActivate)
        // Note: We do NOT register with TurnManager here anymore.
        // Registration happens in onActivate to ensure it persists across scene switches.
        level.actors.forEach(actor => {
            if (!actor.isPlayer && !this.actors.includes(actor)) {
                actor.z = 10; 
                this.add(actor);
            }
        });
        
        // Add items
        level.items.forEach(item => {
            if (!this.actors.includes(item)) this.add(item);
        });
        
        // Add interactables
        level.interactables.forEach(interactable => {
            if (!this.actors.includes(interactable)) this.add(interactable);
        });

        // Add decor
        level.decor.forEach(decor => {
            if (!this.actors.includes(decor)) {
                decor.z = -0.5; // Ensure decor is below entities but above floor
                this.add(decor);
            }
        });
        
        // Ensure graphics are updated
        await level.updateTileGraphics();
    }
    
    public onPreUpdate(engine: ex.Engine, delta: number): void {
        // Clear visibility cache every frame to handle dynamic updates (doors, etc.)
        VisibilitySystem.instance.newFrame();
    }

    public onPostUpdate(engine: ex.Engine, delta: number) {
        // UI updates are handled via events
    }
    
    // Utility methods for external access
    public getHero(): GameActor | null {
        return this.hero;
    }
    
    public isInventoryOpen(): boolean {
        return UIManager.instance.isInventoryVisible;
    }

    public toggleInventory() {
        UIManager.instance.toggleInventory();
    }

    public checkUIHit(screenPos: ex.Vector): boolean {
        return false;
    }
    
    public logCombat(message: string) {
        UIManager.instance.logCombat(message);
    }
    
    public logItem(message: string) {
        UIManager.instance.logItem(message);
    }
    
    public logEffect(message: string) {
        UIManager.instance.logEffect(message);
    }
    
    public logInteraction(message: string) {
        UIManager.instance.logInteraction(message);
    }

    private initializeCanvasUI(hero: GameActor) {
        // Canvas UI removed in favor of DOM UI
    }
}